import React from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import ReactApexChart from "react-apexcharts";
import {useGetShippingDetails} from "../../hooks/useShipping";
import {useLocation, useNavigate} from "react-router-dom";
import moment from "moment-timezone";
import {
    LeftSkeleton,
    MiddleSkeleton,
    RightSkeleton,
    TableSkeleton,
} from "../../component/screening/sub-components/skeleton";

const formatDuration = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) {
        return "0 Minutes";
    }

    if (totalSeconds < 3600) {
        const minutes = totalSeconds / 60;
        return `${minutes.toFixed(2)} Minutes`;
    }

    const hours = Math.floor(totalSeconds / 3600);
    const remainingMinutes = Math.round((totalSeconds % 3600) / 60);

    const hourString = hours === 1 ? '1 Hour' : `${hours} Hours`;

    if (remainingMinutes === 0) {
        return hourString;
    }

    const minuteString = remainingMinutes === 1 ? '1 Minute' : `${remainingMinutes} Minutes`;
    return `${hourString} ${minuteString}`;
};

const DetailsView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {user, startDate, endDate} = location.state || {};

    const startDateString = moment(startDate).format("YYYY-MM-DD");
    const endDateString = moment(endDate).format("YYYY-MM-DD");

    const {data: allDetails, isLoading: isAllDetailsLoading} =
        useGetShippingDetails({
            uid: user?.uid,
            startDate: startDateString,
            endDate: endDateString,
        });

    const chartData = {
        series: [600],

        options: {
            chart: {type: "donut", foreColor: "#ffffff", height: 300, width: 300},
            legend: {show: false},
            dataLabels: {enabled: false},
            tooltip: {enabled: false},
            fill: {
                colors: [
                    allDetails?.user?.colorSelector
                        ? allDetails?.user?.colorSelector
                        : "#7FD320",
                ],
            },
            states: {
                hover: {filter: {type: "lighten", value: 0.5}},
                active: {filter: {type: "none", value: 0}},
            },
            stroke: {width: 0},
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    donut: {
                        size: "60%",
                        labels: {
                            show: true,
                            name: {show: false},
                            total: {
                                show: true,
                                showAlways: true,
                                formatter: function (w) {
                                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                },
                            },
                        },
                    },
                },
            },
        },
    };

    return (
        <>
            <Container fluid>
                <i onClick={() => navigate(-1)} className="fa-solid fa-arrow-left"
                   style={{cursor: "pointer", color: "white"}}/>
                <p className="secondHeader">
                    Shipping Details for {moment(startDate).format("MMM D, YYYY")}
                    {!moment(startDate).isSame(endDate, 'day') && ` to ${moment(endDate).format("MMM D, YYYY")}`}
                </p>

                <Row lg={8} className="justify-content-center">
                    <Col lg={3}>
                        {isAllDetailsLoading ? (
                            <LeftSkeleton/>
                        ) : (
                            <Card className="endingStyle">
                                <Container>
                                    <Row>
                                        <Col style={{paddingTop: "38px"}}>
                                            <h5 style={{textAlign: "center", marginBottom: 5}}>
                                                Period Total
                                            </h5>

                                            <p>
                                                Total Shipped:{" "}
                                                <span style={{display: "inline-block", color: "yellowgreen"}}>
                          {allDetails?.user?.count ?? 0}
                        </span>
                                            </p>
                                            <p>
                                                Average Time Per:{" "}
                                                <span style={{display: "inline-block", color: "yellowgreen"}}>
                          {allDetails?.user?.count > 0
                              ? parseFloat((allDetails?.user?.time / allDetails?.user?.count) / 60).toFixed(2)
                              : '0.00'
                          } Minutes
                        </span>
                                            </p>
                                            <p>
                                                Total Time:{" "}
                                                <span style={{display: "inline-block", color: "yellowgreen"}}>
                          {formatDuration(allDetails?.user?.time)}
                        </span>
                                            </p>
                                            <h5 style={{textAlign: "center", marginBottom: 5, marginTop: 10}}>
                                                Lifetime Averages
                                            </h5>
                                            <p>
                                                Average Time:{" "}
                                                <span style={{display: "inline-block", color: "yellowgreen"}}>
                          {allDetails?.user?.weeklyAverageTime
                              ? parseFloat(allDetails?.user?.weeklyAverageTime).toFixed(2)
                              : 0.0}{" "}
                                                    Minutes
                        </span>
                                            </p>
                                            <p style={{marginTop: 24, marginBottom: 24}}>
                                                Total Quantity:{" "}
                                                <span style={{display: "inline-block", color: "yellowgreen"}}>
                          N/A
                        </span>
                                            </p>
                                        </Col>
                                    </Row>
                                </Container>
                            </Card>
                        )}
                    </Col>
                    <Col lg={4}>
                        {isAllDetailsLoading ? (
                            <MiddleSkeleton/>
                        ) : (
                            <Card className="endingStyle">
                                <h4>
                                    {allDetails?.user?.firstName ?? ""}{" "}
                                    {allDetails?.user?.lastName ?? ""}
                                </h4>
                                <Container>
                                    <Card.Body>
                                        <Card.Title>Items Shipped in Period</Card.Title>
                                        <ReactApexChart
                                            height={300}
                                            options={chartData.options}
                                            series={
                                                allDetails?.user?.count
                                                    ? [allDetails?.user?.count]
                                                    : [0]
                                            }
                                            type="donut"
                                        />
                                    </Card.Body>
                                </Container>
                            </Card>
                        )}
                    </Col>
                    <Col lg={3}>
                        {isAllDetailsLoading ? (
                            <RightSkeleton/>
                        ) : (
                            <Card className="endingStyle">
                                <Container>
                                    <Row>
                                        <Col>
                                            High Score{" "}
                                            <p className="green" style={{fontSize: "26px", paddingLeft: "18px"}}>
                                                {allDetails?.user?.highScore || 0} Packages
                                            </p>
                                            Daily Average Time
                                            <p className="green" style={{fontSize: "26px", paddingLeft: "18px"}}>
                                                {allDetails?.user?.dailyAverage || 0} Minutes
                                            </p>

                                            Daily Average Shipped{" "}
                                            <p className="green" style={{fontSize: "21px"}}>
                                                {Math.round(allDetails?.user?.dailyAverageShipped) || 0} Packages
                                            </p>
                                            Weekly Average Shipped{" "}
                                            <p style={{fontSize: "21px", color: "yellow"}}>
                                                {Math.round(allDetails?.user?.weeklyAverageShipped) || 0} Packages
                                            </p>
                                            Monthly Average Shipped{" "}
                                            <p className="red" style={{fontSize: "21px"}}>
                                                {Math.round(allDetails?.user?.monthlyAverageShipped) || 0} Packages
                                            </p>
                                        </Col>
                                    </Row>
                                </Container>
                            </Card>
                        )}
                    </Col>
                </Row>

                <Row lg={12} className="justify-content-center">
                    {isAllDetailsLoading ? (
                        <TableSkeleton/>
                    ) : (
                        <Col lg={10} className="new-tble-bx">
                            <Table responsive striped bordered hover variant="dark">
                                <thead>
                                <tr>
                                    <th>Order</th>
                                    <th>Tracking Number</th>
                                    <th>Weight</th>
                                    <th>Shipping Charge</th>
                                    <th>Final Shipping Charge</th>
                                    <th>Box Used</th>
                                    <th>Time Spent</th>
                                </tr>
                                </thead>

                                {allDetails?.assets?.length > 0 ? (
                                    <tbody>
                                    {allDetails?.assets?.map((item, i) => (
                                        <tr key={i}>
                                            <td
                                                style={{cursor: "pointer"}}
                                                onClick={() =>
                                                    window.open(
                                                        `https://www.listapp.info/admin/order.php?id=${item?.orderId}`,
                                                        "_blank"
                                                    )
                                                }
                                            >
                                                {item?.order}
                                            </td>
                                            <td>{item?.trackingNumber}</td>
                                            <td>{item?.weight}lbs</td>
                                            <td>${item?.shippingCharge}</td>
                                            <td>${item?.finalShippingCharge}</td>
                                            <td> {item?.box} </td>
                                            <td>
                                                {parseFloat(item?.timeSpent / 60).toFixed(2)}{" "}
                                                Minutes
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                ) : (
                                    <tbody>
                                    <tr>
                                        <td className="text-center" colSpan="7">
                                            No data available for this period
                                        </td>
                                    </tr>
                                    </tbody>
                                )}
                            </Table>
                        </Col>
                    )}
                </Row>
            </Container>
        </>
    );
};

export default DetailsView;